// Benutzerverwaltung (DCP-2228 Folge-Feature) — nur für Admins
//
// Pflegt die Zentral-Mitarbeiter. Depot-Partner und Depot-Mitarbeiter erscheinen hier
// bewusst nicht: die kommen aus den commercetools-Depotstammdaten und würden sich
// beim nächsten Abgleich überschreiben.
//
// Nicht sichtbar ist der Boden im Code (`config/user-permissions.ts`): die dort
// eingetragenen Administratoren behalten ihre Rechte, egal was hier steht. Das ist
// der Schutz davor, sich selbst auszuschließen — und es bedeutet, dass ein Entzug
// hier für diese Adressen wirkungslos bleibt. Der Hinweis unter der Tabelle sagt das.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { ApiError } from '@/lib/api-client';
import { useMe } from '@/hooks/usePermissions';
import {
  useUsers,
  usePermissionCatalog,
  useSaveUser,
  useDeleteUser,
} from '@/services/user-service';
import type { ManagedUser, UserFilters as Filters } from '@/types';
import { UserFormDialog } from './UserFormDialog';
import { UserFilters } from './UserFilters';
import { filterUsers } from './user-filter';

export function UserListPage() {
  const { t } = useTranslation('users');
  const { toast } = useToast();

  const { data: me } = useMe();
  const { data: users = [], isLoading, isError, error } = useUsers();
  const { data: catalog = {} } = usePermissionCatalog();

  const saveMutation = useSaveUser();
  const deleteMutation = useDeleteUser();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ManagedUser | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ManagedUser | null>(null);
  const [filters, setFilters] = useState<Filters>({});

  const currentEmail = me?.email ?? '';
  const isDenied = error instanceof ApiError && (error.status === 401 || error.status === 403);

  // Client-side only: the directory covers central staff, a small and stable
  // set, unlike the events feature's depot-scale lists — no need for a
  // server-side filter/pagination path (see manage-users.ts, which loads
  // everything in one shot already).
  const visibleUsers = useMemo(() => filterUsers(users, filters), [users, filters]);

  const handleSave = (user: ManagedUser) => {
    saveMutation.mutate(user, {
      onSuccess: () => {
        toast({ title: t('toast.saved') });
        setDialogOpen(false);
      },
      // The server rejects self-demotion and removing the last admin with a German
      // message; show it rather than a generic failure, since it explains the rule.
      onError: (e) =>
        toast({
          title: t('toast.error'),
          description: e instanceof Error ? e.message : String(e),
          variant: 'destructive',
        }),
    });
  };

  const handleDelete = (user: ManagedUser) => {
    deleteMutation.mutate(user.email, {
      onSuccess: () => {
        toast({ title: t('toast.deleted') });
        setConfirmDelete(null);
      },
      onError: (e) =>
        toast({
          title: t('toast.error'),
          description: e instanceof Error ? e.message : String(e),
          variant: 'destructive',
        }),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('page.title')}</h2>
          <p className="text-muted-foreground">{t('page.description')}</p>
        </div>
        <Button
          className="gap-2"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          {t('actions.create')}
        </Button>
      </div>

      {isLoading ? (
        <p className="p-8 text-center text-muted-foreground">{t('page.loading')}</p>
      ) : isDenied ? (
        <p className="p-8 text-center text-muted-foreground">{t('page.noAccess')}</p>
      ) : isError ? (
        <p className="p-8 text-center text-red-600">{t('page.loadError')}</p>
      ) : users.length === 0 ? (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          {t('page.empty')}
        </p>
      ) : (
        <>
          <UserFilters filters={filters} onChange={setFilters} />

          {visibleUsers.length === 0 ? (
            <p className="rounded-md border p-8 text-center text-muted-foreground">
              {t('filters.noMatches')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('table.email')}</TableHead>
                  <TableHead>{t('table.role')}</TableHead>
                  <TableHead>{t('table.permissions')}</TableHead>
                  <TableHead>{t('table.updated')}</TableHead>
                  <TableHead className="text-right">{t('table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleUsers.map((user) => (
                  <TableRow key={user.email}>
                    <TableCell className="font-medium">
                      {user.email}
                      {user.email.toLowerCase() === currentEmail.toLowerCase() && (
                        <span className="ml-2 text-xs text-muted-foreground">{t('table.you')}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {t(`role.${user.role}`)}
                      </Badge>
                      {user.canSwitchRole && (
                        <span className="ml-2 inline-flex" title={t('table.canSwitchRole')}>
                          <UserCog className="h-3.5 w-3.5 text-muted-foreground" />
                        </span>
                      )}
                      {/* Abweichung gegenüber commercetools. Die gespeicherte Rolle gilt —
                          das hier ist ein Hinweis für den Admin, keine Vorgabe. Fehlt, wenn
                          commercetools nicht erreichbar war. */}
                      {user.ctRole && (
                        <div
                          className="mt-1 text-xs text-amber-600"
                          title={t('ctRoleDivergesHint')}
                        >
                          {t('ctRoleDiverges', { role: t(`role.${user.ctRole}` as never) })}
                        </div>
                      )}
                      {/* Wartet auf Freigabe: automatisch angelegt, noch nichts entschieden.
                          Ohne diese Kennzeichnung wäre „wartet" von „bewusst nichts erlaubt"
                          nicht unterscheidbar, und niemand wüsste, wo etwas zu tun ist. */}
                      {user.approvalState === 'pending' && (
                        <div
                          className="mt-1 text-xs text-muted-foreground"
                          title={t('approvalPendingHint')}
                        >
                          {t('approvalPending')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {summarizePermissions(user.permissions) || t('table.noPermissions')}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {user.updatedAt ? formatDay(user.updatedAt) : '—'}
                      {user.updatedBy && <div className="truncate">{user.updatedBy}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(user);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete(user)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      <p className="text-xs text-muted-foreground">{t('page.codeFloorNote')}</p>

      <UserFormDialog
        open={dialogOpen}
        user={editing}
        catalog={catalog}
        currentUserEmail={currentEmail}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isSaving={saveMutation.isPending}
      />

      <ConfirmationDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t('delete.title')}
        description={t('delete.description', { email: confirmDelete?.email ?? '' })}
        confirmLabel={t('delete.confirm')}
        variant="destructive"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
      />
    </div>
  );
}

/** "Veranstaltungen: 4, Rezensionen: 2" — kompakt, weil die Spalte schmal ist. */
function summarizePermissions(permissions: Record<string, string[]>): string {
  return Object.entries(permissions)
    .filter(([, actions]) => actions.length > 0)
    .map(([resource, actions]) => `${resource}: ${actions.length}`)
    .join(', ');
}

function formatDay(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return day && month && year ? `${day}.${month}.${year}` : iso;
}
